/**
 * Most mezi DealerHub "Spare Parts Finder" web-componentou a košíkem
 * Staněk Moto (ClickUp #86c9w3x13).
 *
 * Poslouchá event `spfAddToCart`, zavolá serverový signál `addToCart!`
 * (SparePartsPresenter) a zpět widgetu pošle `spfAddToCartProcessed`.
 * Kontrakty viz docs/spare-parts-finder-widget/reference/events.md
 */

interface SpfAddToCartDetail {
    articleNumber: string;
    brand?: string;
    quantity?: number;
    clientArticleNumber?: string;
}

interface SpfAddToCartResponse {
    // naja formát: id snippetu → HTML (překreslený hlavičkový košík)
    snippets?: Record<string, string>;
    spfAddToCart?: { articleNumber: string; success: boolean };
}

const successfulArticleNumbers = new Set<string>();

// Snippety hlavičkového košíku (top-bar.latte). Aplikujeme JEN tyhle — odpověď
// signálu může nést i další snippety (head, a na detailu produktu i `tabs`, který
// obaluje sám widget), a jejich přepsáním by se widget resetoval.
const basketHeaderSnippetIds = ["snippet--basketHeader", "snippet--basketHeaderMobile"];

/** Aplikuje z AJAX odpovědi jen snippety hlavičkového košíku (id → nové innerHTML). */
function applySnippets(snippets: Record<string, string> | undefined): void {
    if (snippets === undefined) {
        return;
    }

    for (const id of basketHeaderSnippetIds) {
        const html = snippets[id];
        const element = document.getElementById(id);

        if (html !== undefined && element !== null) {
            element.innerHTML = html;
        }
    }
}

function readDetail(event: Event): SpfAddToCartDetail | null {
    const custom = event as CustomEvent;
    // Komponenta obaluje payload do detail.detail; fallback na detail.
    const raw = custom.detail?.detail ?? custom.detail;

    if (raw && typeof raw.articleNumber === "string" && raw.articleNumber !== "") {
        return raw as SpfAddToCartDetail;
    }

    return null;
}

function dispatchProcessed(): void {
    document.dispatchEvent(new CustomEvent("spfAddToCartProcessed", {
        detail: {
            successfulArticleNumbers: Array.from(successfulArticleNumbers),
            errorArticleNumbers: [] as string[],
        },
    }));
}

function dispatchError(articleNumber: string): void {
    document.dispatchEvent(new CustomEvent("spfAddToCartProcessed", {
        detail: {
            successfulArticleNumbers: Array.from(successfulArticleNumbers),
            errorArticleNumbers: [articleNumber],
        },
    }));
}

async function handleAddToCart(url: string, event: Event): Promise<void> {
    const detail = readDetail(event);

    if (detail === null) {
        return;
    }

    try {
        const response = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-Requested-With": "XMLHttpRequest",
            },
            body: JSON.stringify({
                articleNumber: detail.articleNumber,
                brand: detail.brand ?? "",
                quantity: detail.quantity ?? 1,
                // U nahrazeného dílu nese widget SKU nástupce z našeho spfArticleInfos;
                // server ho preferuje, aby do košíku padl skutečně nabídnutý díl.
                clientArticleNumber: detail.clientArticleNumber ?? "",
            }),
        });

        const result = await response.json() as SpfAddToCartResponse;

        // Signál překreslí hlavičkový košík (naja snippety) — aplikujeme je, aby
        // se ikona/počet aktualizovaly bez reloadu.
        applySnippets(result.snippets);

        if (response.ok && result.spfAddToCart?.success === true) {
            successfulArticleNumbers.add(detail.articleNumber);
            dispatchProcessed();
        } else {
            dispatchError(detail.articleNumber);
        }
    } catch (e) {
        dispatchError(detail.articleNumber);
    }
}

interface SpfArticleInfoRequestItem {
    articleNumber: string;
    brand?: string;
}

function readArticleItems(event: Event): SpfArticleInfoRequestItem[] {
    const custom = event as CustomEvent;
    // Komponenta obaluje payload do detail.detail; fallback na detail.
    const raw = custom.detail?.detail ?? custom.detail;
    const items = raw?.items;

    if (!Array.isArray(items)) {
        return [];
    }

    return items.filter((item): item is SpfArticleInfoRequestItem =>
        item && typeof item.articleNumber === "string" && item.articleNumber !== "");
}

async function handleArticleInfos(url: string, event: Event): Promise<void> {
    const articleNumbers = readArticleItems(event).map((item) => item.articleNumber);

    if (articleNumbers.length === 0) {
        return;
    }

    try {
        const response = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-Requested-With": "XMLHttpRequest",
            },
            body: JSON.stringify({ articleNumbers }),
        });

        if (!response.ok) {
            return;
        }

        const result = await response.json() as { infos: ArticleInfo[] };
        const infos = result.infos ?? [];

        rememberReplacements(infos);

        document.dispatchEvent(new CustomEvent("spfArticleInfos", {
            detail: { infos },
        }));

        // Seznam dílů se právě otevřel, takže shadow root už stojí i tehdy, když
        // se `spfOnWebComponentLoaded` minulo s registrací listeneru.
        setUpWidgetShadowRoot();
    } catch (e) {
        // Ticho — bez cen/skladovosti widget zobrazí díly bez těchto údajů.
    }
}

interface ArticleInfo {
    articleNumber: string;
    clientArticleNumber?: string;
}

/**
 * Nahrazené díly: původní číslo → číslo nástupce. Server u náhrady vrací
 * `clientArticleNumber` (SKU nástupce) pod původním KTM article numberem, takže
 * mapu plníme z téže odpovědi, kterou přeposíláme widgetu.
 */
const replacedBy = new Map<string, string>();

function rememberReplacements(infos: ArticleInfo[]): void {
    for (const info of infos) {
        const successor = info.clientArticleNumber;

        if (typeof successor === "string" && successor !== "" && successor !== info.articleNumber) {
            replacedBy.set(info.articleNumber, successor);
        }
    }
}

// Vlastní značka místo MUI tříd — `data-testid` widget používá pro e2e, takže
// je stabilnější než jeho interní class names.
const articleNumberTestId = "sparePartsFinder.subgroupPage.sparePartsList.grid.cell.articleNumber";
const replacedClass = "spf-article-number--replaced";
const replacementClass = "spf-article-number--replacement";
const styleElementId = "spf-project-styles";

/** Shadow root widgetu (mode:"open"), nebo null než se komponenta namountuje. */
function widgetShadowRoot(): ShadowRoot | null {
    const widget = document.querySelector("pmag-spare-parts-finder");

    return (widget as (Element & { shadowRoot?: ShadowRoot }) | null)?.shadowRoot ?? null;
}

/**
 * Projektové styly do shadow rootu widgetu. Zvenčí se dovnitř nedostaneme
 * a widget nemá pro tohle žádný hook, ale shadow root je `open`.
 */
function injectStyles(shadow: ShadowRoot): void {
    if (shadow.getElementById(styleElementId) !== null) {
        return;
    }

    const style = document.createElement("style");
    style.id = styleElementId;
    style.textContent = `
        /* Tabulka dílů potřebuje 855 px (sloupce 60 + 230 + 165 + 400). Split.js
           panel stahuje níž a poslední sloupec s tlačítkem se pak ořízne; šířku
           zapisuje inline, takže ji přebije jedině min-width.

           Rozpočet je těsný a musí se vejít do Bootstrap containeru: od xl má
           1140 px, widget v něm 1116 px, a 855 + gutter + 230 = 1115 px. Pod
           xl je container jen 960 px, kam se to nevejde — pravidlo by vytlačilo
           stránku do vodorovného posuvníku (ověřeno na 1024 px), takže tam radši
           necháme gridu jeho vlastní posuvník. Gutter se smrskne z 30 na ~19 px,
           to je v pořádku; nákresu zbyde ~242 px. */
        @media (min-width: 1200px) {
            div:has(> .gutter.gutter-horizontal) > div:first-child { min-width: 855px; }
            .gutter.gutter-horizontal + div { min-width: 230px; }
        }

        /* Dvě čísla vedle sebe (původní + nástupce) se vejdou jen se zalomením —
           widget má buňky na nowrap. Zalamuje se jen řádek s číslem, ne název. */
        [data-testid="${articleNumberTestId}"] { white-space: normal; }

        .spf-article-number--replaced { text-decoration: line-through; opacity: .55; }
        .spf-article-number--replacement { font-weight: 600; }
    `;

    shadow.append(style);
}

/**
 * U nahrazeného dílu přeškrtne původní číslo a vedle něj dopíše číslo nástupce.
 * Zákazník tak u starého čísla vidí, co mu reálně přijde — dřív to bylo jen
 * v textu skladovosti, kde se to do úzkého sloupce nevešlo.
 */
function decorateReplacements(): void {
    const shadow = widgetShadowRoot();

    if (shadow === null || replacedBy.size === 0) {
        return;
    }

    const cells = shadow.querySelectorAll<HTMLElement>(`[data-testid="${articleNumberTestId}"]`);

    for (const cell of Array.from(cells)) {
        // Hotovost poznáme podle vlastní značky, ne podle atributu: grid při
        // virtualizaci elementy recykluje a přepíše jim jen text, takže atribut
        // by přežil a buňka by zůstala nedotčená.
        if (cell.querySelector(`.${replacementClass}`) !== null) {
            continue;
        }

        const original = (cell.textContent ?? "").trim();
        const successor = replacedBy.get(original);

        if (successor === undefined) {
            continue;
        }

        cell.textContent = "";

        const strikethrough = document.createElement("span");
        strikethrough.className = replacedClass;
        strikethrough.textContent = original;

        const replacement = document.createElement("span");
        replacement.className = replacementClass;
        replacement.textContent = successor;

        cell.append(strikethrough, document.createTextNode(" "), replacement);
    }
}

let decorationObserver: MutationObserver | null = null;
let watchedRoot: ShadowRoot | null = null;
let decorationQueued = false;

/**
 * Grid tabulku přerendruje po každé odpovědi s cenami i při přepnutí podskupiny
 * a React přitom buňce přepíše `textContent`, čímž naše spany zmizí — observer
 * je vrací zpět. Při vlastním zápisu se musí odpojit, jinak se spustí sám od
 * sebe donekonečna (ověřeno: dřívější verze prohlížeč zatuhla).
 */
function watchReplacements(shadow: ShadowRoot): void {
    if (watchedRoot === shadow) {
        return;
    }

    // Nová instance widgetu = nový shadow root; starý sledovat nemá smysl.
    decorationObserver?.disconnect();
    watchedRoot = shadow;

    const options: MutationObserverInit = { childList: true, subtree: true };

    const observer: MutationObserver = new MutationObserver(() => {
        if (decorationQueued) {
            return;
        }

        decorationQueued = true;
        requestAnimationFrame(() => {
            decorationQueued = false;

            // Mezitím mohl vzniknout jiný shadow root — tenhle už se nesleduje.
            if (watchedRoot !== shadow) {
                return;
            }

            observer.disconnect();
            decorateReplacements();
            observer.observe(shadow, options);
        });
    });

    decorationObserver = observer;
    observer.observe(shadow, options);
}

/** Styly i observer dávají smysl až nad namountovaným widgetem. */
function setUpWidgetShadowRoot(): void {
    const shadow = widgetShadowRoot();

    if (shadow === null) {
        return;
    }

    injectStyles(shadow);
    decorateReplacements();
    watchReplacements(shadow);
}

interface SpfSendRequestDetail {
    articleNumber: string;
    brand?: string;
}

function readSendRequestDetail(event: Event): SpfSendRequestDetail | null {
    const custom = event as CustomEvent;
    // Komponenta obaluje payload do detail.detail; fallback na detail.
    const raw = custom.detail?.detail ?? custom.detail;

    if (raw && typeof raw.articleNumber === "string" && raw.articleNumber !== "") {
        return raw as SpfSendRequestDetail;
    }

    return null;
}

/**
 * Best-effort přečtení aktuálně vybraného modelu z widgetu. Model není v žádném
 * eventu (ověřeno), jen v hlavičce jeho shadow DOM (např. "690 SMC R 2012" v
 * nadpisu). Je to křehká vazba na cizí strukturu — proto try/catch a při jakékoli
 * nejistotě null (zpráva se pak složí bez modelu).
 */
function readSelectedModel(container: HTMLElement): string | null {
    try {
        const widget = container.querySelector("pmag-spare-parts-finder");
        const shadow = (widget as (Element & { shadowRoot?: ShadowRoot }) | null)?.shadowRoot;

        if (!shadow) {
            return null;
        }

        const headings = shadow.querySelectorAll("h1, h2, h3, h4, h5, h6");

        for (const heading of Array.from(headings)) {
            const text = (heading.textContent ?? "").trim().replace(/\s+/g, " ");

            // Hlavička modelu začíná kubaturou (číslo) a obsahuje písmena; ostatní
            // nadpisy (názvy skupin, titulek widgetu) tomuhle neodpovídají.
            if (/^\d{2,4}\b/.test(text) && /[A-Za-z]/.test(text) && text.length <= 40) {
                return text;
            }
        }

        return null;
    } catch (e) {
        return null;
    }
}

function buildInquiryMessage(articleNumber: string, brand: string, model: string | null): string {
    const brandPart = brand !== "" ? ` (${brand})` : "";
    const modelPart = model !== null ? ` pro model ${model}` : "";

    return `Dobrý den, mám zájem o díl č. ${articleNumber}${brandPart}${modelPart}, `
        + "který je momentálně nedostupný. Prosím o informaci k dostupnosti nebo náhradě.";
}

const inquiryId = "spare-parts-inquiry";
const inquiryTriggerId = "spare-parts-inquiry-trigger";

/**
 * Plynulý scroll na kotvu se stejným offsetem pod fixní menu, jaký dělají kotvy
 * jinde na webu (AnimatedAnchors v assets-base). Vlastní kopie proto, že sdílená
 * komponenta scroll nevystavuje — pracuje jen deklarativně nad
 * `<a data-animated-anchor>`, kdežto sem se scrolluje z custom eventu widgetu.
 * Stejný vzor drží i B2B collapse v index.ts; při sjednocování jsou to tři místa.
 */
function scrollToAnchor(target: HTMLElement): void {
    const nav = document.querySelector("nav");

    target.style.scrollMarginTop = `${nav !== null ? nav.clientHeight * 1.5 : 0}px`;
    target.scrollIntoView({ block: "start", behavior: "smooth" });
}

/**
 * Odhalí poptávkový formulář (v šabloně je defaultně `d-none`) a sjede k němu.
 * Odkaz, který ho vyvolal, zároveň schová — po odhalení nemá co dělat.
 */
function revealInquiry(): HTMLElement | null {
    const inquiry = document.getElementById(inquiryId);

    if (inquiry === null) {
        return null;
    }

    inquiry.classList.remove("d-none");
    document.getElementById(inquiryTriggerId)?.classList.add("d-none");

    scrollToAnchor(inquiry);

    return inquiry;
}

/**
 * Odkaz „nenašli jste díl?" pod vyhledávačem. Je to jediná cesta k formuláři ve
 * chvíli, kdy widget tlačítko „Send request" nenabídne — to má jen u nalezeného
 * nedostupného dílu, ne u prázdného výsledku hledání.
 */
function bindInquiryTrigger(): void {
    const trigger = document.querySelector<HTMLAnchorElement>(`#${inquiryTriggerId} a`);

    if (trigger === null) {
        return;
    }

    trigger.addEventListener("click", (event: Event) => {
        event.preventDefault();

        const inquiry = revealInquiry();

        // Odkaz se odhalením sám schová, takže by fokus spadl na <body> a uživatel
        // na klávesnici by se ztratil — přesuň ho na první pole formuláře.
        // preventScroll: fokus by jinak skokem přebil rozjetý plynulý scroll.
        inquiry?.querySelector<HTMLElement>("input:not([type=hidden]), textarea, select")
            ?.focus({ preventScroll: true });
    });
}

/**
 * Obsluha eventu `spfSendRequestForArticle` (tlačítko „Send request" u nedostupného
 * dílu). Bez redirectu: předvyplní zprávou kontaktní formulář dole na stránce
 * (číslo dílu + best-effort model), odhalí ho a odscrolluje k němu.
 */
function handleSendRequest(container: HTMLElement, event: Event): void {
    const detail = readSendRequestDetail(event);

    if (detail === null) {
        return;
    }

    const textarea = document.querySelector<HTMLTextAreaElement>(`#${inquiryId} textarea`);

    if (textarea === null) {
        return;
    }

    const model = readSelectedModel(container);
    textarea.value = buildInquiryMessage(detail.articleNumber, detail.brand ?? "", model);
    // input event kvůli případnému FormPersisteru / listenerům na změnu hodnoty.
    textarea.dispatchEvent(new Event("input", { bubbles: true }));

    revealInquiry();
    // preventScroll: fokus skrytého pole by jinak skočil skokem a přebil rozjetý plynulý scroll.
    textarea.focus({ preventScroll: true });
}

export function initialize(): void {
    // Před guardem níž — odkaz na formulář má fungovat i tam, kde vyhledávač není
    // (nenakonfigurovaný widget vyrenderuje místo sebe varovnou hlášku).
    bindInquiryTrigger();

    // Skrytý formulář nezná ani prohlížeč, takže příchod na URL s #spare-parts-inquiry
    // (sdílený odkaz, e-mail) by jinak skončil nikde — odhal ho a sjeď k němu.
    if (window.location.hash === `#${inquiryId}`) {
        revealInquiry();
    }

    const container = document.getElementById("spare-parts-finder");

    if (container === null) {
        return;
    }

    const addToCartUrl = container.getAttribute("data-spf-add-to-cart-url");

    if (addToCartUrl !== null && addToCartUrl !== "") {
        document.addEventListener("spfAddToCart", (event: Event) => {
            void handleAddToCart(addToCartUrl, event);
        });
    }

    const articleInfosUrl = container.getAttribute("data-spf-article-infos-url");

    if (articleInfosUrl !== null && articleInfosUrl !== "") {
        document.addEventListener("spfRequestArticleInfosForArticles", (event: Event) => {
            void handleArticleInfos(articleInfosUrl, event);
        });
    }

    // Send-request u nedostupného dílu předvyplní kontaktní formulář dole na
    // stránce (žádný redirect). Vyžaduje sendRequestEnabled: true v config widgetu
    // a #spare-parts-inquiry s formulářem v šabloně.
    document.addEventListener("spfSendRequestForArticle", (event: Event) => {
        handleSendRequest(container, event);
    });

    // Widget hlásí namountování; na detailu motorky se skript navíc natahuje
    // lazy, takže shadow root v tu chvíli ještě existovat nemusí.
    document.addEventListener("spfOnWebComponentLoaded", setUpWidgetShadowRoot);
    setUpWidgetShadowRoot();
}
